<template>
  <div class="cmt-panel">

    <!-- List -->
    <div class="cmt-list" ref="listEl">

      <div v-if="isLoading" class="cmt-empty">
        <b-spinner small variant="secondary" />
      </div>
      <div v-else-if="!comments.length" class="cmt-empty">
        <feather-icon icon="MessageCircleIcon" size="28" class="text-muted mb-50" />
        <p class="mb-0 text-muted" style="font-size: 0.82rem;">No comments yet</p>
      </div>

      <div v-for="(comment, idx) in comments" :key="'cmt-' + idx" class="cmt-row">
        <b-avatar
          :src="comment.fullfilepath || require('@/assets/images/avatars/8-small.png')"
          size="30px"
          class="cmt-avatar"
        />
        <div class="cmt-bubble">
          <div class="cmt-meta">
            <span class="cmt-author">{{ comment.created_name }}</span>
            <div class="d-flex align-items-center" style="gap: 0.4rem;">
              <span class="cmt-time">{{ comment.created_at | fmtDate }}</span>
              <feather-icon
                v-if="userData && userData.id === comment.created_by"
                icon="Trash2Icon"
                size="11"
                class="cmt-delete"
                @click="deleteComment(comment.id)"
              />
            </div>
          </div>
          <div class="cmt-text" v-html="comment.comment" />
        </div>
      </div>

      <b-pagination
        v-if="rows > perPage"
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        size="sm"
        class="mt-1 mb-0 justify-content-center"
      />
    </div>

    <!-- Input -->
    <div class="cmt-input-wrap" :style="{ height: inputAreaHeight + 'px' }">
      <!-- Height resize handle -->
      <div class="input-resize-handle" @mousedown="startInputResize" />
      <validation-observer ref="rules">
        <validation-provider rules="required" name="comment" v-slot="{ errors }">
          <quill-editor
            v-model="input_comment.comment"
            :options="editorOptions"
            class="cmt-editor"
          />
          <small v-if="errors[0]" class="text-danger d-block mt-25">{{ errors[0] }}</small>
        </validation-provider>
        <div class="d-flex align-items-center justify-content-between mt-50">
          <small class="text-muted" style="font-size: 0.72rem;">Ctrl + Enter to send</small>
          <b-button variant="primary" size="sm" :disabled="isSubmitting" @click="submit">
            <b-spinner v-if="isSubmitting" small class="mr-25" />
            <feather-icon v-else icon="SendIcon" size="13" class="mr-25" />Send
          </b-button>
        </div>
      </validation-observer>
    </div>

  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { BButton, BAvatar, BPagination, BSpinner } from 'bootstrap-vue'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'
import axios from '@axios'
import moment from 'moment'

export default {
  components: { ValidationProvider, ValidationObserver, BButton, BAvatar, BPagination, BSpinner, quillEditor },
  filters: {
    fmtDate: v => v ? moment(v).format('DD/MM/YY HH:mm') : '',
  },
  props: {
    id:   { type: [String, Number] },
    type: { type: String, default: 'property' },
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem('userData')),
      input_comment: { title: 'Comment', comment: null, created_by: null, property_id: this.id, house_id: this.id },
      comments: [],
      currentPage: 1,
      perPage: 15,
      rows: 0,
      isLoading: false,
      isSubmitting: false,
      inputAreaHeight: 200,
      editorOptions: {
        placeholder: 'Write a comment…',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
          ],
        },
      },
    }
  },
  watch: {
    currentPage() { this.load() },
  },
  created() { this.load() },
  methods: {
    startInputResize(e) {
      e.preventDefault()
      const startY = e.clientY
      const startH = this.inputAreaHeight
      const onMove = ev => {
        const delta = startY - ev.clientY
        this.inputAreaHeight = Math.min(500, Math.max(120, startH + delta))
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    async load() {
      this.isLoading = true
      try {
        if (this.type === 'house') {
          const r = await axios.post('v1/house/comment', { types: 'comment', perPage: this.perPage, currentPage: this.currentPage, house_id: this.id })
          if (r.data.success) { this.comments = r.data.data; this.rows = r.data.total }
        } else {
          const r = await axios.post('property/v1/getComments/' + this.id, JSON.stringify({ types: 'comment', perPage: this.perPage, Offsets: this.perPage * (this.currentPage - 1) }))
          if (r.data.success) { this.comments = r.data.data; this.rows = r.data.total }
        }
      } finally {
        this.isLoading = false
        this.$nextTick(() => { if (this.$refs.listEl) this.$refs.listEl.scrollTop = 0 })
      }
    },
    async submit() {
      const ok = await this.$refs.rules.validate()
      if (!ok) return
      this.isSubmitting = true
      this.input_comment.created_by = this.userData.id
      try {
        const ep = this.type === 'house' ? 'v1/house/comment/add' : 'property/v1/add_comment'
        const r = await axios.post(ep, JSON.stringify(this.input_comment))
        if (r.data.success) {
          this.input_comment.comment = null
          this.$refs.rules.reset()
          this.currentPage = 1
          await this.load()
        }
        this.$bvToast.toast(r.data.message, { title: r.data.success ? 'Added' : 'Error', variant: r.data.success ? 'success' : 'danger', solid: true, toaster: 'b-toaster-top-right' })
      } catch {
        this.$bvToast.toast('Failed to add comment', { title: 'Error', variant: 'danger', solid: true, toaster: 'b-toaster-top-right' })
      } finally {
        this.isSubmitting = false
      }
    },
    deleteComment(id) {
      this.$bvModal.msgBoxConfirm('Delete this comment?', { title: 'Confirm', size: 'sm', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true })
        .then(ok => {
          if (!ok) return
          const ep = this.type === 'house' ? `v1/house/comment/delete/${id}` : `property/v1/delteComment/${id}`
          axios.delete(ep).then(() => {
            this.$bvToast.toast('Comment deleted', { title: 'Done', variant: 'success', solid: true, toaster: 'b-toaster-top-right' })
            this.currentPage = 1
            this.load()
          })
        })
    },
  },
}
</script>

<style scoped>
.cmt-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

/* scrollable list */
.cmt-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cmt-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

/* comment row */
.cmt-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
}
.cmt-avatar { flex-shrink: 0; margin-top: 2px; }

.cmt-bubble {
  position: relative;
  background: #fff;
  border-radius: 0 12px 12px 12px;
  padding: 0.5rem 0.75rem 0.45rem;
  flex: 1;
  min-width: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border: 1px solid #f0f0f0;
}
.cmt-bubble::before {
  content: '';
  position: absolute;
  top: 0;
  left: -6px;
  width: 0;
  height: 0;
  border-top: 6px solid #fff;
  border-left: 6px solid transparent;
}

.cmt-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  gap: 0.5rem;
}
.cmt-author {
  font-size: 0.78rem;
  font-weight: 700;
  color: #5e5873;
}
.cmt-time {
  font-size: 0.69rem;
  color: #b9b9c3;
  white-space: nowrap;
}
.cmt-delete {
  color: #ea5455;
  cursor: pointer;
  opacity: 0.5;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.cmt-delete:hover { opacity: 1; }

.cmt-text {
  font-size: 0.83rem;
  color: #4b465c;
  line-height: 1.55;
  word-break: break-word;
}

/* input */
.cmt-input-wrap {
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid #ebe9f1;
  background: #fff;
  overflow: hidden;
}
.input-resize-handle {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 6px;
  cursor: row-resize;
  z-index: 5;
  background: transparent;
  transition: background 0.15s;
}
.input-resize-handle:hover { background: rgba(115, 103, 240, 0.2); }

.cmt-editor { border-radius: 6px; overflow: hidden; flex: 1; display: flex; flex-direction: column; }
.cmt-editor ::v-deep .ql-toolbar { border-color: #ebe9f1; padding: 4px 8px; flex-shrink: 0; }
.cmt-editor ::v-deep .ql-container { border-color: #ebe9f1; font-size: 0.84rem; flex: 1; overflow-y: auto; }
.cmt-editor ::v-deep .ql-editor { min-height: 60px; }
.cmt-editor ::v-deep .ql-editor.ql-blank::before { color: #b9b9c3; font-style: normal; }
</style>
